import { getTypeaheadSuggestions, TypeaheadSuggestionsGetOptions } from 'api/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'

export interface SearchTypeaheadOptions extends TypeaheadSuggestionsGetOptions {
  /**
   * Minimum length for the search term before search is triggered. (inclusive)
   *
   * @default 3
   */
  searchTermThreshold?: number
  /**
   * Debounce waiting type per input.
   *
   * @default 300
   *  */
  wait?: number
}

interface TypeaheadResult {
  searchItems: Array<App.SearchItem>
  searchTerm: string
}

function useSearchTypeahead(
  searchTerm: string,
  {
    searchTermThreshold = 3,
    wait = 300,
    types,
    searchTypes,
    vertical,
    priority,
    limit,
    canViewLuxPlusBenefits,
  }: SearchTypeaheadOptions = EmptyObject,
): App.StatefulData<TypeaheadResult> {
  const [typeahead, setTypeahead] = useState<App.StatefulData<TypeaheadResult>>({ fetching: false, data: { searchItems: EmptyArray, searchTerm: '' } })
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const fetch = useMemo(() => debounce(async(input: string) => {
    setTypeahead({ fetching: true })

    try {
      const result = await getTypeaheadSuggestions(
        input,
        currentRegionCode,
        {
          types,
          searchTypes,
          vertical,
          priority,
          limit,
          canViewLuxPlusBenefits,
        },
      )
      setTypeahead({
        fetching: false,
        data: {
          searchItems: result.length ? result : EmptyArray,
          searchTerm: input,
        },
      })
    } catch (error) {
      setTypeahead({
        fetching: false,
        error: String(error.message ?? error),
      })
    }
  }, wait), [canViewLuxPlusBenefits, currentRegionCode, limit, priority, searchTypes, types, vertical, wait])

  useEffect(() => {
    if (searchTerm.length >= searchTermThreshold) {
      fetch(searchTerm)
    }
  }, [searchTerm, searchTermThreshold, fetch])

  return typeahead
}

export default useSearchTypeahead
